/**
 * Semantic icon vocabulary — the design-system-canonical set of
 * meanings every theme-bound icon family must cover. Names align with
 * lucide because lucide is the default family + majority current usage.
 *
 * Each per-family map under `./sets/<family>.ts` MUST export a
 * `Record<IconName, string>` so any vocabulary growth here is enforced
 * across families at compile time (missing entries fail typecheck).
 *
 * Single API: every icon — chrome (close X, chevrons, loader) and
 * semantic (status, action, entity) — is a vocabulary entry resolved
 * through the active theme family. A tenant whose theme is phosphor
 * gets phosphor chevrons too; visual identity stays coherent across
 * the entire UI. The lone exception is `Icon`'s legacy `icon=` prop,
 * which renders a literal `family:name` (e.g. `svg-spinners:...`) for
 * one-off icons outside this vocabulary.
 */

export const ICON_VOCABULARY = [
  // Status / feedback
  'alert-circle',
  'alert-triangle',
  'check',
  'check-circle',
  'info',
  'x-circle',

  // Chrome — UI mechanics. Theme-resolved (single API) but conceptually
  // structural rather than content-bearing. Default to aria-hidden in
  // call sites (decorative); only set aria-label when the icon IS the
  // affordance label (e.g. a bare-X close button without text).
  'close',
  'menu',
  'chevron-up',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'chevrons-left',
  'chevrons-right',
  'chevrons-up-down',
  'more-horizontal',
  'more-vertical',
  'grip-vertical',
  'grip-horizontal',
  'loader',

  // Action — generic
  'copy',
  'download',
  'upload',
  'pencil',
  'plus',
  'minus',
  'refresh',
  'rotate-ccw',
  'undo',
  'redo',
  'send',
  'trash',
  'trash-2',
  'update',
  'view-column',
  'filter',
  'search',
  'scan-eye',
  'eye',
  'eye-off',
  'external-link',
  'pause',
  'play',

  // Action — auth/security
  'lock',
  'unlock',
  'log-out',
  'shield',
  'shield-alert',
  'shield-check',
  'help-circle',
  'store',
  'package',

  // Navigation — directional (semantic, not chrome chevrons)
  'arrow-up',
  'arrow-down',
  'arrow-left',
  'arrow-right',
  'arrow-up-right',

  // Entity
  'briefcase',
  'building',
  'users',
  'user',
  'map-pin',
  'inbox',

  // Content type
  'book-open',
  'chart-line',
  'code',
  'code-json',
  'file',
  'file-code',
  'file-excel',
  'file-json',
  'file-outline',
  'file-table',
  'file-text',
  'film',
  'image',
  'printer',

  // Time
  'calendar',
  'calendar-range',
  'clock',

  // Engagement / visual flag
  'circle',
  'flame',
  'ghost',
  'layers',
  'palette',
  'star',
  'star-off',
  'star-four-points',
  'trending-up',
  'trending-down',
  'heart',
  'thumbs-up',
  'bell',
  'tag',

  // Filesystem
  'folder',
  'folder-open',
  'cloud-upload',
  'camera',

  // Theme / appearance
  'moon',
  'sun',

  // External services / links
  'github',
  'globe',

  // Domain-specific (CMMS / data lineage)
  'compare',
  'cube',
  'flask-conical',
  'package-down',
  'rocket-launch',
  'settings',
  'settings-2',
  'tree',
] as const

export type IconName = (typeof ICON_VOCABULARY)[number]

/**
 * Locale-bound aria-label for each semantic icon. Consumers can ship
 * their own override map; this default covers en + tr so the design
 * system is usable without an i18n integration step.
 *
 * Add a new locale by extending each entry; missing locales fall back
 * to `en` at render time (handled in `Icon.vue`).
 */
/**
 * Per-concept aria-label translations. The two canonical locales are
 * `en` (always) + `tr` (always); consumers can register additional
 * locales for *extended* vocabulary via `ExtendedVocabularyEntry.labels`.
 * Built-in concepts ship en+tr by hand — adding more here is an
 * intentional vocabulary expansion, not runtime-extensible.
 */
export const ICON_LABELS: Record<IconName, { en: string, tr: string, [locale: string]: string | undefined }> = {
  // Status
  'alert-circle': { en: 'Alert', tr: 'Uyarı' },
  'alert-triangle': { en: 'Warning', tr: 'Dikkat' },
  'check': { en: 'Confirm', tr: 'Onayla' },
  'check-circle': { en: 'Success', tr: 'Başarılı' },
  'info': { en: 'Information', tr: 'Bilgi' },
  'x-circle': { en: 'Error', tr: 'Hata' },

  // Chrome
  'close': { en: 'Close', tr: 'Kapat' },
  'menu': { en: 'Menu', tr: 'Menü' },
  'chevron-up': { en: 'Collapse', tr: 'Daralt' },
  'chevron-down': { en: 'Expand', tr: 'Genişlet' },
  'chevron-left': { en: 'Previous', tr: 'Önceki' },
  'chevron-right': { en: 'Next', tr: 'Sonraki' },
  'chevrons-left': { en: 'First', tr: 'Başa git' },
  'chevrons-right': { en: 'Last', tr: 'Sona git' },
  'chevrons-up-down': { en: 'Sort', tr: 'Sırala' },
  'more-horizontal': { en: 'More', tr: 'Daha fazla' },
  'more-vertical': { en: 'More', tr: 'Daha fazla' },
  'grip-vertical': { en: 'Drag', tr: 'Sürükle' },
  'grip-horizontal': { en: 'Drag', tr: 'Sürükle' },
  'loader': { en: 'Loading', tr: 'Yükleniyor' },

  // Action — generic
  'copy': { en: 'Copy', tr: 'Kopyala' },
  'download': { en: 'Download', tr: 'İndir' },
  'upload': { en: 'Upload', tr: 'Yükle' },
  'pencil': { en: 'Edit', tr: 'Düzenle' },
  'plus': { en: 'Add', tr: 'Ekle' },
  'minus': { en: 'Remove', tr: 'Çıkar' },
  'refresh': { en: 'Refresh', tr: 'Yenile' },
  'rotate-ccw': { en: 'Undo', tr: 'Geri Al' },
  'undo': { en: 'Undo', tr: 'Geri al' },
  'redo': { en: 'Redo', tr: 'Yinele' },
  'send': { en: 'Send', tr: 'Gönder' },
  'trash': { en: 'Delete', tr: 'Sil' },
  'trash-2': { en: 'Delete', tr: 'Sil' },
  'update': { en: 'Update', tr: 'Güncelle' },
  'view-column': { en: 'Column View', tr: 'Sütun Görünümü' },
  'filter': { en: 'Filter', tr: 'Filtrele' },
  'search': { en: 'Search', tr: 'Ara' },
  'scan-eye': { en: 'Inspect', tr: 'İncele' },
  'eye': { en: 'Show', tr: 'Göster' },
  'eye-off': { en: 'Hide', tr: 'Gizle' },
  'external-link': { en: 'Open externally', tr: 'Dış bağlantı' },
  'pause': { en: 'Pause', tr: 'Duraklat' },
  'play': { en: 'Play', tr: 'Oynat' },

  // Action — auth/security
  'lock': { en: 'Lock', tr: 'Kilitle' },
  'unlock': { en: 'Unlock', tr: 'Kilidi Aç' },
  'log-out': { en: 'Sign out', tr: 'Çıkış yap' },
  'shield': { en: 'Security', tr: 'Güvenlik' },
  'shield-alert': { en: 'Security alert', tr: 'Güvenlik uyarısı' },
  'shield-check': { en: 'Verified', tr: 'Doğrulandı' },
  'help-circle': { en: 'Help', tr: 'Yardım' },
  'store': { en: 'Store', tr: 'Mağaza' },
  'package': { en: 'Package', tr: 'Paket' },

  // Navigation — directional
  'arrow-up': { en: 'Up', tr: 'Yukarı' },
  'arrow-down': { en: 'Down', tr: 'Aşağı' },
  'arrow-left': { en: 'Back', tr: 'Geri' },
  'arrow-right': { en: 'Forward', tr: 'İleri' },
  'arrow-up-right': { en: 'Open', tr: 'Aç' },

  // Entity
  'briefcase': { en: 'Work', tr: 'İş' },
  'building': { en: 'Organisation', tr: 'Kuruluş' },
  'users': { en: 'Team', tr: 'Ekip' },
  'user': { en: 'Person', tr: 'Kişi' },
  'map-pin': { en: 'Location', tr: 'Konum' },
  'inbox': { en: 'Inbox', tr: 'Gelen kutusu' },

  // Content type
  'book-open': { en: 'Documentation', tr: 'Dokümantasyon' },
  'chart-line': { en: 'Chart', tr: 'Grafik' },
  'code': { en: 'Code', tr: 'Kod' },
  'code-json': { en: 'JSON', tr: 'JSON' },
  'file': { en: 'File', tr: 'Dosya' },
  'file-code': { en: 'Source file', tr: 'Kaynak dosya' },
  'file-excel': { en: 'Spreadsheet', tr: 'Hesap tablosu' },
  'file-json': { en: 'JSON file', tr: 'JSON dosya' },
  'file-outline': { en: 'File', tr: 'Dosya' },
  'file-table': { en: 'Tabular file', tr: 'Tablo dosya' },
  'file-text': { en: 'Document', tr: 'Belge' },
  'film': { en: 'Video', tr: 'Video' },
  'image': { en: 'Image', tr: 'Görsel' },
  'printer': { en: 'Print', tr: 'Yazdır' },

  // Time
  'calendar': { en: 'Calendar', tr: 'Takvim' },
  'calendar-range': { en: 'Date range', tr: 'Tarih aralığı' },
  'clock': { en: 'Time', tr: 'Saat' },

  // Engagement / visual flag
  'circle': { en: 'Indicator', tr: 'Gösterge' },
  'flame': { en: 'Trending', tr: 'Yükselen' },
  'ghost': { en: 'Empty', tr: 'Boş' },
  'layers': { en: 'Layers', tr: 'Katmanlar' },
  'palette': { en: 'Theme', tr: 'Tema' },
  'star': { en: 'Favourite', tr: 'Favori' },
  'star-off': { en: 'Unfavourite', tr: 'Favoriden çıkar' },
  'star-four-points': { en: 'New', tr: 'Yeni' },
  'trending-up': { en: 'Trending up', tr: 'Yükselen' },
  'trending-down': { en: 'Trending down', tr: 'Düşen' },
  'heart': { en: 'Like', tr: 'Beğen' },
  'thumbs-up': { en: 'Approve', tr: 'Onayla' },
  'bell': { en: 'Notifications', tr: 'Bildirimler' },
  'tag': { en: 'Tag', tr: 'Etiket' },

  // Filesystem
  'folder': { en: 'Folder', tr: 'Klasör' },
  'folder-open': { en: 'Open folder', tr: 'Açık klasör' },
  'cloud-upload': { en: 'Upload to cloud', tr: 'Buluta yükle' },
  'camera': { en: 'Camera', tr: 'Kamera' },

  // Theme
  'moon': { en: 'Dark mode', tr: 'Karanlık mod' },
  'sun': { en: 'Light mode', tr: 'Açık mod' },

  // External services
  'github': { en: 'GitHub', tr: 'GitHub' },
  'globe': { en: 'Web', tr: 'Web' },

  // Domain-specific
  'compare': { en: 'Compare', tr: 'Karşılaştır' },
  'cube': { en: 'Asset', tr: 'Varlık' },
  'flask-conical': { en: 'Experiment', tr: 'Deney' },
  'package-down': { en: 'Install', tr: 'Yükle' },
  'rocket-launch': { en: 'Launch', tr: 'Başlat' },
  'settings': { en: 'Settings', tr: 'Ayarlar' },
  'settings-2': { en: 'Advanced settings', tr: 'Gelişmiş ayarlar' },
  'tree': { en: 'Hierarchy', tr: 'Hiyerarşi' },
}

/**
 * Built-in locale keys present in every `ICON_LABELS` entry. Use this
 * for autocomplete in consumer code that targets the canonical
 * locales. The `IconLocale` type below opens to arbitrary strings so
 * extended vocabulary entries can register locales beyond en/tr
 * (e.g. de, ja, ar) without forking vocabulary.ts.
 */
export type IconBuiltInLocale = keyof (typeof ICON_LABELS)[IconName]

/**
 * Concepts whose visual meaning is *direction-bound*. In RTL writing
 * systems (Arabic, Farsi, Hebrew) a "next" chevron points LEFT, not
 * right; rendering the unflipped lucide:chevron-right inside an RTL
 * paragraph reads as "previous" to the user.
 *
 * `Icon.vue` consults this set when `dir="rtl"` is on (or inherited
 * from) the rendered SVG's nearest container and applies a
 * `scaleX(-1)` transform. The transform is opt-out per call via the
 * `flip` prop:
 *   - `'auto'` (default) — flip if direction-bound AND container is RTL
 *   - `true`  — always flip
 *   - `false` — never flip (e.g. for a graphic that already encodes
 *               direction independently)
 *
 * Icons not in this set are unaffected — a `home` glyph means the
 * same thing in any writing direction.
 */
// `log-in` would be a natural fit too but isn't in the built-in
// ICON_VOCABULARY (only `log-out`). Consumer apps that register
// `log-in` via `extendVocabulary()` can add it to their own
// directional set if they need RTL flipping.
export const DIRECTIONAL_ICONS: ReadonlySet<IconName> = new Set<IconName>([
  'chevron-left',
  'chevron-right',
  'chevrons-left',
  'chevrons-right',
  'arrow-left',
  'arrow-right',
  'arrow-up-right',
  'log-out',
])

/**
 * Locale tag accepted by `Icon`'s aria-label resolver. Either one of
 * the canonical built-in locales (autocomplete) or any tenant-defined
 * locale string (BCP-47 tag). Falls back to `'en'` when the requested
 * locale isn't present on the concept's label entry.
 */
export type IconLocale = IconBuiltInLocale | (string & {})
